//@ts-check
import { useContext, useState, useEffect, useRef, useCallback, useMemo, createContext } from "react"
import { startOfMonth, endOfMonth, subMonths, getUnixTime, format } from "date-fns"
import { fromZonedTime } from 'date-fns-tz'
import useRawPromise from "../../hooks/useRawPromise"
import TimezoneContext from "../Timezone"
import TestCategoriesContext from "../TestCategories"
import { stripClub, stripTestCategory, stripAnalyses } from '../../utils/stripData'
import useUnit from "../../hooks/useUnit"
import PitchListContext from "../PitchList"
import LocaleContext from "../Locale"
import useBridge from "../../hooks/useBridge"
import { useIntl } from "react-intl"
import ProfileContext from "../Profile"
import fetchPDF from '../../utils/fetchPDF'
import { deviceMeasurementTypesToClean, filterTestCategoriesOfAnalyses, getConvertedEventUnitsTuples, getTotalProgress, handleUnitConvertEvent } from "../../utils/pdfGenerator"

/** @type {import('react').Context<any>} */
const GeneralReportContext = createContext(undefined)
export default GeneralReportContext

const SECTION_NAMES = {
    coverPage: 'coverPage',
    testCategories: 'testCategories',
    testsOverview: 'testsOverview',
    testsBar: 'testsBar',
    calendarTotals: 'calendarTotals',
    contactPage: 'contactPage',
}

export function GeneralReportProvider({ children, ...props }) {
    const { get } = useRawPromise()
    const { convertToUnits, formatUnitName } = useUnit()
    const { formatMessage } = useIntl()
    const { datefnsLocale, locale } = useContext(LocaleContext)
    const { data: eventTypes, loading: loadingEventTypes } = useBridge('/api/current/frontend-bridge/event-types')
    const { data: profile } = useContext(ProfileContext)

    //period
    const { reporterTz } = useContext(TimezoneContext)
    const [endDate, setEndDate] = useState(endOfMonth(subMonths(new Date(), 1)))
    const [startDate, setStartDate] = useState(startOfMonth(subMonths(new Date(), 1)))
    const { unixStart, unixEnd } = useMemo(() => {
        // handle timezone shenanigans as determined in the user's setting (timezone for reporter)
        const start = getUnixTime(fromZonedTime(startDate, reporterTz))
        const end = getUnixTime(fromZonedTime(endDate, reporterTz))
        // handle interval gracefully, even if user's start is later than the end
        const reversed = (start > end)
        return {
            unixStart: reversed ? end : start,
            unixEnd: reversed ? start : end,
        }
    }, [reporterTz, endDate, startDate])

    //pitches
    /** @type {[any, Function]} */
    const [selectedPitches, setSelectedPitches] = useState([])
    const { defaultOrderPitches } = useContext(PitchListContext) // sorted according to sort rank

    // tests
    const { data: testCategories = [], busy: testCategoriesBusy } = useContext(TestCategoriesContext)
    const [selectedTestCategories, setSelectedTestCategories] = useState([])
    const secondStepValid = useMemo(() => {
        return !testCategoriesBusy && selectedTestCategories.length > 0
    }, [testCategoriesBusy, selectedTestCategories])

    // options
    const [reportTitle, setReportTitle] = useState(formatMessage({ id: 'monthReport' }))
    const [organisationName, setOrganisationName] = useState('')
    const [includeCoverPage, setIncludeCoverPage] = useState(true)
    const [includeLogo, setIncludeLogo] = useState(true)
    const [includeTestCatDescription, setIncludeTestCatDescription] = useState(false)
    const [includeTestsOverview, setIncludeTestsOverview] = useState(true)
    const [includeTestsBarCharts, setIncludeTestsBarCharts] = useState(true)
    const [includeCalendarTotals, setIncludeCalendarTotals] = useState(true)
    const [includeContactPage, setIncludeContactPage] = useState(true)

    const [avoidColorConflicts, setAvoidColorConflicts] = useState(false)
    const [printFriendly, setPrintFriendly] = useState(false)

    //contexts to fetch
    const [includeTests, setIncludeTests] = useState(true)
    const [includeCalendar, setIncludeCalendar] = useState(true)

    const handleSetIncludeTests = useCallback(() => {
        setIncludeTests(curr => {
            setIncludeTestsOverview(!curr)
            setIncludeTestsBarCharts(!curr)
            return !curr
        })
    }, [])

    const handleSetIncludeCalendar = useCallback(() => {
        setIncludeCalendar(curr => {
            setIncludeCalendarTotals(!curr)
            return !curr
        })
    }, [])

    const firstStepValid = useMemo(() => {
        return selectedPitches.length > 0 && (includeTests || includeCalendar) && !loadingEventTypes
    }, [selectedPitches, includeTests, includeCalendar, loadingEventTypes])

    // data
    /** @type {[any, Function]} */
    const [data, setData] = useState({})  //BIIIIG Data

    /** @type {[{ msg: string, progress: number } | undefined, Function]} */
    const [fetchingBusyState, setFetchingBusyState] = useState() // {msg, progress}

    const dataReady = useMemo(() => {
        return fetchingBusyState?.progress === 100
    }, [fetchingBusyState])

    const hasOrgNameBeenSet = useRef(false)

    useEffect(() => {
        hasOrgNameBeenSet.current = false
        setOrganisationName('')
    }, [selectedPitches])

    useEffect(() => {
        if (!hasOrgNameBeenSet.current && data?.club?.name) {
            setOrganisationName(data.club.name)
            hasOrgNameBeenSet.current = true
        }
    }, [data])

    // request abortion logic
    const allowFetchRef = useRef(true)
    const abortFetches = useCallback(() => {
        allowFetchRef.current = false
    }, [])
    const allowFetches = useCallback(() => {
        allowFetchRef.current = true
    }, [])
    useEffect(() => {
        return abortFetches
    }, [])

    const handleFetchAllData = useCallback(async () => {
        const progressWeights = [5, 10, 50, 35]

        setData({})

        // add a list of pitch details following pitchSortRank order 
        const selectedPitchIds = selectedPitches.map(p => p.value)
        setData({
            pitches: defaultOrderPitches
                .filter((pitch) => selectedPitchIds.includes(pitch.id))
                .map((pitch, i) => ({ ...pitch, pitchSortRank: i }))
        })

        //club
        try {
            //Keep It Simple, Stupid! It's a hassle to check all clubs of pitches with current api. 
            //So we check the club on the fist pitch, and give an option to not use a specific club in case of multiple
            setFetchingBusyState({
                msg: formatMessage({ id: 'fetchingClub' }),
                progress: 0,
            })
            const firstPitchId = selectedPitches[0]?.value
            if (!allowFetchRef.current) return
            const { data: pitch } = await get(`/api/current/pitches/${firstPitchId}`)
            const club = pitch?.club
            if (!club) {
                throw new Error('Pitch or club data is missing')
            }

            setFetchingBusyState({
                msg: formatMessage({ id: 'clubFetched' }),
                progress: getTotalProgress([
                    100,
                    includeTests ? 0 : undefined,
                    includeTests ? 0 : undefined,
                    includeCalendar ? 0 : undefined,
                ], progressWeights),
            })
            stripClub(club)
            setData(prev => ({ ...prev, club }))
        } catch (e) {
            console.log(e)
            setFetchingBusyState({
                msg: formatMessage({ id: 'couldNotRetrieveClubError' }),
                progress: 0
            })
            abortFetches()
            return setData()
        }

        if (includeTests) {
            try {
                // FETCH TESTCATEGORIES
                const testCategoriesBuffer = []
                let catCount = 1
                for (const catId of selectedTestCategories) {
                    setFetchingBusyState({
                        msg: formatMessage(
                            { id: 'fetchingCategoryXOfY', },
                            {
                                catCount,
                                catTotal: selectedTestCategories.length,
                            },
                        ),
                        progress: getTotalProgress([
                            100,
                            catCount / selectedTestCategories.length * 100,
                            0,
                            includeCalendar ? 0 : undefined,
                        ], progressWeights),
                    })
                    catCount++
                    if (!allowFetchRef.current) return
                    const { data: category } = await get(`/api/current/test-categories/${catId}`)
                    testCategoriesBuffer.push(category)
                }
                // mutate data to make lightweight and converted to units
                for (const cat of testCategoriesBuffer) {

                    const doConvert = !!cat.unitSI

                    stripTestCategory(cat)

                    const convert = (val) => convertToUnits(val, cat.unitSI, { metricPrecision: cat.allowedDecimals, useSecondaryUnit: false })
                    const conversionObj = convert(1)
                    const convertedUnitName = conversionObj.converted ? conversionObj.primaryUnit?.name : cat.unitSI
                    const convertedPrecision = conversionObj.converted ? conversionObj.primaryUnit?.convertedPrecision : cat.allowedDecimals

                    cat.convertedUnit = convertedUnitName
                    cat.convertedDecimals = doConvert ? convertedPrecision : cat.allowedDecimals

                    if (doConvert) {
                        cat.maxRatingValue = convert(cat.maxRatingValue).primaryValue
                        cat.minRatingValue = convert(cat.minRatingValue).primaryValue
                        cat.ratings = cat.ratings.map(rating => {
                            return {
                                ...rating,
                                max: convert(rating.max).primaryValue,
                                min: convert(rating.min).primaryValue,
                            }
                        })
                    }
                }

                setData(prev => ({ ...prev, testCategories: testCategoriesBuffer }))

                // FETCH ANALYSES
                let analysesBuffer = []
                let pitchCount = 1
                setFetchingBusyState({
                    msg: formatMessage({ id: 'collectingTests' }),
                    progress: getTotalProgress([
                        100,
                        100,
                        0,
                        includeCalendar ? 0 : undefined,
                    ], progressWeights),
                })
                for (const pitch of selectedPitches) {
                    const pitchId = pitch.value

                    if (!allowFetchRef.current) return
                    const { data } = await get(
                        `/api/current/pitches/${pitchId}/analyses?from=${unixStart}&to=${unixEnd}&itemsPerPage=20`,
                        { unpackHydra: false }
                    )
                    analysesBuffer.push(...data['hydra:member'])

                    // fetch other pages if needed
                    let currentData = data
                    while (true) {
                        const nextPage = currentData['hydra:view']?.['hydra:next']
                        if (!nextPage) break
                        if (!allowFetchRef.current) return
                        const { data: nextData } = await get(nextPage, { unpackHydra: false })
                        analysesBuffer.push(...nextData['hydra:member'])
                        currentData = nextData
                    }
                    setFetchingBusyState({
                        msg: formatMessage({ id: 'searchedXanalysesFromYofZPitches' }, {
                            analCount: analysesBuffer.length,
                            pitchCount,
                            pitchTotal: selectedPitches.length,
                        }),
                        progress: getTotalProgress([
                            100,
                            100,
                            pitchCount / selectedPitches.length * 100,
                            includeCalendar ? 0 : undefined,
                        ], progressWeights),
                    })
                    pitchCount++
                }

                filterTestCategoriesOfAnalyses(analysesBuffer, selectedTestCategories)
                stripAnalyses(analysesBuffer)
                // filter out analyses with no tests left
                analysesBuffer = analysesBuffer.filter(anal => (Array.isArray(anal.tests) && anal.tests.length > 0))
                // convert values to user's unit system
                analysesBuffer.forEach(anal => {
                    anal.tests.forEach(test => {
                        const { allowedDecimals, unitSI, measurementType } = testCategoriesBuffer.find(cat => cat.id === test.testCategory)
                        test.zoneResults.forEach(zR => {
                            // quick dirty fix to clean zeros from digital devices. TO DO: firmware needs to avoid this
                            if (zR.val === 0 && deviceMeasurementTypesToClean.includes(measurementType)) zR.val = null

                            zR.val = convertToUnits(zR.val, unitSI, { metricPrecision: allowedDecimals, useSecondaryUnit: false }).primaryValue
                        })
                    })
                })
                setData(prev => ({ ...prev, analyses: analysesBuffer }))
            } catch (e) {
                console.log(e)
                setFetchingBusyState({
                    msg: formatMessage({ id: 'couldNotRetrieveAnalysesError' }),
                    progress: 0
                })
                abortFetches()
                return setData()
            }
        }

        if (includeCalendar) {
            try {
                const eventsBuffer = []
                let pitchCount = 1
                setFetchingBusyState({
                    msg: formatMessage({ id: 'collectingEvents' }),
                    progress: getTotalProgress([
                        100,
                        100,
                        includeTests ? 100 : undefined,
                        0,
                    ], progressWeights),
                })

                for (const pitch of selectedPitches) {
                    const pitchId = pitch.value

                    if (!allowFetchRef.current) return
                    const { data } = await get(
                        `/api/current/pitches/${pitchId}/events?from=${unixStart}&to=${unixEnd}`,
                        { unpackHydra: false }
                    )
                    eventsBuffer.push(...data['hydra:member'])

                    // fetch other pages if needed
                    let currentData = data
                    while (true) {
                        const nextPage = currentData['hydra:view']?.['hydra:next']
                        if (!nextPage) break
                        if (!allowFetchRef.current) return
                        const { data: nextData } = await get(nextPage, { unpackHydra: false })
                        eventsBuffer.push(...nextData['hydra:member'])
                        currentData = nextData
                    }
                    setFetchingBusyState({
                        msg: formatMessage({ id: 'collectedXeventsFromYofZPitches' }, {
                            eventCount: eventsBuffer.length,
                            pitchCount,
                            pitchTotal: selectedPitches.length,
                        }),
                        progress: getTotalProgress([
                            100,
                            100,
                            includeTests ? 100 : undefined,
                            pitchCount / selectedPitches.length * 100,
                        ], progressWeights),
                    })
                    pitchCount++
                }

                for (const event of eventsBuffer) {
                    handleUnitConvertEvent(event, convertToUnits)
                }

                setData(prev => ({
                    ...prev,
                    events: eventsBuffer,
                    eventTypes,
                    convertedEventUnits: getConvertedEventUnitsTuples(formatUnitName),
                }))
            } catch (e) {
                console.log(e)
                setFetchingBusyState({
                    msg: formatMessage({ id: 'couldNotRetrieveEventsError' }),
                    progress: 0
                })
                abortFetches()
                return setData()
            }
        }

        setFetchingBusyState({ progress: 100 })

    }, [unixStart, unixEnd, selectedPitches, defaultOrderPitches, selectedTestCategories, includeTests, includeCalendar, eventTypes])

    const retryFetchData = useCallback(() => {
        setData({})
        allowFetches()
        handleFetchAllData()
    }, [])


    const pdfDefaultState = {
        download: () => { },
        cleanup: () => { },
        busy: false,
        error: undefined,
    }

    /** @type {[any, Function]} */
    const [pdf, setPdf] = useState(pdfDefaultState)

    const submitReportGenerationRequest = useCallback(async () => {
        if (!data) return
        pdf.cleanup()
        setPdf({
            ...pdfDefaultState,
            busy: true,
        })

        const sectionsToExclude = [
            !includeCoverPage && SECTION_NAMES.coverPage,
            !includeTestCatDescription && SECTION_NAMES.testCategories,
            !includeTestsOverview && SECTION_NAMES.testsOverview,
            !includeTestsBarCharts && SECTION_NAMES.testsBar,
            !includeCalendarTotals && SECTION_NAMES.calendarTotals,
            !includeContactPage && SECTION_NAMES.contactPage,
        ].filter(v => !!v)

        const payloadJSON = {
            report: {
                reportType: 'general',
                reportTitle,
                organisationName,
                includeLogo,
                logoSrc: data.club?.logo?.file,
                formattedFromDate: format(startDate, 'PP', { locale: datefnsLocale }),
                formattedToDate: format(endDate, 'PP', { locale: datefnsLocale }),
                sectionsToExclude,
                locale,
                avoidColorConflicts,
                reporterTz,
                printFriendly,
            },
            ...data
        }

        delete payloadJSON.club  // avoid confusion on the server side

        try {
            const fileName = profile.admin ?
                `${organisationName} ${payloadJSON.report.formattedFromDate} - ${payloadJSON.report.formattedToDate}.pdf`
                : `${reportTitle}.pdf`

            const { a, revokeURL } = await fetchPDF(payloadJSON, fileName)

            setPdf({
                busy: false,
                download: () => a.click(),
                cleanup: () => {
                    revokeURL()
                    setPdf(pdfDefaultState)
                }
            })

        } catch (error) {
            console.error('Error generating pdf:', error)
            const err =
                typeof error === 'object' && error !== null && 'response' in error
                    ? error.response
                    : error
            setPdf({
                ...pdfDefaultState,
                error: err,
                busy: false,
            })
        }
    }, [
        data, pdf, datefnsLocale, locale, startDate, endDate,
        reportTitle, organisationName, includeCoverPage, includeTestsOverview,
        includeTestsBarCharts, includeCalendarTotals, includeContactPage, includeLogo, profile,
        avoidColorConflicts, reporterTz, printFriendly
    ])

    return (
        <GeneralReportContext.Provider
            value={{
                endDate, startDate, setEndDate, setStartDate,
                includeTests, includeCalendar, handleSetIncludeTests, handleSetIncludeCalendar,
                selectedPitches, setSelectedPitches,
                testCategories, selectedTestCategories, setSelectedTestCategories,
                allowFetches, abortFetches, handleFetchAllData, fetchingBusyState, dataReady, retryFetchData,
                firstStepValid, secondStepValid,
                reportTitle, setReportTitle, organisationName, setOrganisationName,
                includeCoverPage, setIncludeCoverPage,
                includeTestCatDescription, setIncludeTestCatDescription,
                includeTestsOverview, setIncludeTestsOverview,
                includeTestsBarCharts, setIncludeTestsBarCharts,
                includeCalendarTotals, setIncludeCalendarTotals,
                includeContactPage, setIncludeContactPage,
                includeLogo, setIncludeLogo,
                data,
                submitReportGenerationRequest,
                pdf,
                avoidColorConflicts, setAvoidColorConflicts,
                printFriendly, setPrintFriendly,
            }}
            {...props}
        >
            {children}
        </GeneralReportContext.Provider>
    )
}